import { useState, useCallback, useRef, useEffect } from "react"
import { Bug, Lightbulb, Spinner, Info } from "@phosphor-icons/react"
import { toast } from "sonner"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../../../components/ui/sheet"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import { Label } from "../../../components/ui/label"
import { Textarea } from "../../../components/ui/textarea"
import { TicketFileDropzone } from "./TicketFileDropzone"
import { useCreateSystemTicket } from "../hooks/useCreateSystemTicket"
import { useUploadSystemTicketAttachment } from "../hooks/useUploadSystemTicketAttachment"
import { captureDeviceContext } from "../utils/device-context"
import { useErrorOverlayStore } from "../../../stores/error-overlay-store"
import { useAuthStore } from "../../../stores/auth-store"
import { useUIStore } from "../../../stores/ui-store"
import type { CreateSystemTicketPayload, DiagnosticData } from "../types"

interface SystemTicketDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

type Category = "bug" | "feature_request"

function Hint({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs text-muted-foreground flex items-start gap-1.5 mt-1">
      <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
      <span>{children}</span>
    </p>
  )
}

function RequiredDot() {
  return <span className="text-destructive ml-0.5">*</span>
}

export function SystemTicketDrawer({ open, onOpenChange }: SystemTicketDrawerProps) {
  const [category, setCategory] = useState<Category | null>(null)
  const [subject, setSubject] = useState("")
  const [description, setDescription] = useState("")
  const [stepsTaken, setStepsTaken] = useState("")
  const [expectedBehavior, setExpectedBehavior] = useState("")
  const [externalReference, setExternalReference] = useState("")
  const [files, setFiles] = useState<File[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  const deviceContextRef = useRef(captureDeviceContext())

  const createTicket = useCreateSystemTicket()
  const uploadAttachment = useUploadSystemTicketAttachment()

  const resetForm = useCallback(() => {
    setCategory(null)
    setSubject("")
    setDescription("")
    setStepsTaken("")
    setExpectedBehavior("")
    setExternalReference("")
    setFiles([])
    setIsSubmitting(false)
  }, [])

  // When drawer opens: capture context, auto-fill page URL
  useEffect(() => {
    if (open) {
      deviceContextRef.current = captureDeviceContext()
      setExternalReference(window.location.href)
    }
  }, [open])

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) resetForm()
      onOpenChange(nextOpen)
    },
    [onOpenChange, resetForm]
  )

  const handleSubmit = useCallback(async () => {
    if (!category) {
      toast.error("Please select a ticket type")
      return
    }
    if (!subject.trim()) {
      toast.error("Please enter a subject")
      return
    }
    if (!description.trim()) {
      toast.error(
        category === "bug"
          ? "Please describe what you were trying to do"
          : "Please describe what you'd like"
      )
      return
    }
    if (category === "bug" && !stepsTaken.trim()) {
      toast.error("Please describe the steps you took — this helps us reproduce the issue")
      return
    }
    if (category === "feature_request" && !externalReference.trim()) {
      toast.error("Please provide a link to the page where you'd like this feature")
      return
    }

    setIsSubmitting(true)

    try {
      const MAX_STACK = 3000
      const errors = useErrorOverlayStore.getState().errors.slice(0, 10)
      const diagnostic_data: DiagnosticData = {
        recent_errors: errors.map((e) => ({
          message: e.message,
          source: e.source,
          stack: e.stack?.slice(0, MAX_STACK),
          componentStack: e.componentStack?.slice(0, MAX_STACK),
          url: e.url,
          fileLocation: e.fileLocation,
          timestamp: e.timestamp,
          count: e.count,
        })),
        user_context: {
          privileges: useAuthStore.getState().user?.privileges || [],
          selected_division: useUIStore.getState().selectedDivision,
        },
      }

      const payload: CreateSystemTicketPayload = {
        category,
        subject: subject.trim(),
        description: description.trim(),
        steps_taken: stepsTaken.trim() || undefined,
        expected_behavior: expectedBehavior.trim() || undefined,
        device_context: deviceContextRef.current,
        diagnostic_data,
      }
      if (externalReference.trim()) payload.external_reference = externalReference.trim()

      const result = await createTicket.mutateAsync(payload)
      const ticketId = result.data?._id

      if (ticketId && files.length > 0) {
        for (const file of files) {
          try {
            await uploadAttachment.mutateAsync({ ticketId, file })
          } catch {
            // Continue uploading remaining files
          }
        }
      }

      toast.success("Ticket submitted — we'll look into this")
      handleOpenChange(false)
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to submit ticket"
      toast.error(message)
    } finally {
      setIsSubmitting(false)
    }
  }, [
    category,
    subject,
    description,
    stepsTaken,
    expectedBehavior,
    externalReference,
    files,
    createTicket,
    uploadAttachment,
    handleOpenChange,
  ])

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader className="mb-6">
          <SheetTitle>Submit a Ticket</SheetTitle>
          <SheetDescription>
            The more detail you provide, the faster we can help. Fields marked with <span className="text-destructive">*</span> are required.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-5">
          {/* Category selection */}
          <div className="space-y-2">
            <Label>What can we help with?<RequiredDot /></Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setCategory("bug")}
                className={`flex flex-col items-center gap-2 rounded-lg border-2 p-4 transition-colors ${
                  category === "bug"
                    ? "border-primary bg-primary/5 text-primary"
                    : "border-border hover:border-primary/30 text-muted-foreground hover:text-foreground"
                }`}
              >
                <Bug className="h-6 w-6" weight={category === "bug" ? "fill" : "regular"} />
                <span className="text-sm font-medium">Report a Bug</span>
              </button>
              <button
                type="button"
                onClick={() => setCategory("feature_request")}
                className={`flex flex-col items-center gap-2 rounded-lg border-2 p-4 transition-colors ${
                  category === "feature_request"
                    ? "border-primary bg-primary/5 text-primary"
                    : "border-border hover:border-primary/30 text-muted-foreground hover:text-foreground"
                }`}
              >
                <Lightbulb
                  className="h-6 w-6"
                  weight={category === "feature_request" ? "fill" : "regular"}
                />
                <span className="text-sm font-medium">Request a Feature</span>
              </button>
            </div>
          </div>

          {/* Show form only after category is selected */}
          {category && (
            <>
              {/* Subject */}
              <div className="space-y-1">
                <Label htmlFor="ticket-subject">Subject<RequiredDot /></Label>
                <Input
                  id="ticket-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  maxLength={200}
                />
                <Hint>
                  {category === "bug"
                    ? "A brief title — avoid generic phrases like \"it doesn't work\" or \"something is broken\"."
                    : "A brief title for your request."}
                </Hint>
              </div>

              {/* --- BUG-SPECIFIC FIELDS --- */}
              {category === "bug" && (
                <>
                  {/* What were you trying to do? */}
                  <div className="space-y-1">
                    <Label htmlFor="ticket-description">What were you trying to do?<RequiredDot /></Label>
                    <Textarea
                      id="ticket-description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={3}
                    />
                    <Hint>
                      Describe the action you were performing when the issue occurred.
                    </Hint>
                  </div>

                  {/* Steps taken */}
                  <div className="space-y-1">
                    <Label htmlFor="ticket-steps">Steps you took before the issue<RequiredDot /></Label>
                    <Textarea
                      id="ticket-steps"
                      value={stepsTaken}
                      onChange={(e) => setStepsTaken(e.target.value)}
                      rows={5}
                    />
                    <Hint>
                      Walk us through it step by step. The more detail here, the faster we can reproduce and fix it.
                    </Hint>
                  </div>

                  {/* Expected vs Actual */}
                  <div className="space-y-1">
                    <Label htmlFor="ticket-expected">What did you expect to happen?</Label>
                    <Textarea
                      id="ticket-expected"
                      value={expectedBehavior}
                      onChange={(e) => setExpectedBehavior(e.target.value)}
                      rows={3}
                    />
                    <Hint>
                      Describe what the correct behavior should look like vs. what actually happened.
                    </Hint>
                  </div>

                  {/* External reference for bugs */}
                  <div className="space-y-1">
                    <Label htmlFor="ticket-external-ref">Page URL or Reference</Label>
                    <Input
                      id="ticket-external-ref"
                      value={externalReference}
                      onChange={(e) => setExternalReference(e.target.value)}
                      placeholder="URL or description of where the issue occurred"
                    />
                  </div>
                </>
              )}

              {/* --- FEATURE REQUEST FIELDS --- */}
              {category === "feature_request" && (
                <>
                  {/* What would you like? */}
                  <div className="space-y-1">
                    <Label htmlFor="ticket-description">What would you like to be able to do?<RequiredDot /></Label>
                    <Textarea
                      id="ticket-description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={4}
                    />
                    <Hint>
                      Describe the outcome you want and what it should do.
                    </Hint>
                  </div>

                  {/* Page link */}
                  <div className="space-y-1">
                    <Label htmlFor="ticket-external-ref-feature">
                      Link to the page where you want this feature<RequiredDot />
                    </Label>
                    <Input
                      id="ticket-external-ref-feature"
                      value={externalReference}
                      onChange={(e) => setExternalReference(e.target.value)}
                      placeholder="Paste a URL or describe the location in the app"
                    />
                    <Hint>
                      Paste a URL or describe where in the app this would live.
                    </Hint>
                  </div>

                  {/* How do you handle it today? */}
                  <div className="space-y-1">
                    <Label htmlFor="ticket-steps">How do you currently handle this?</Label>
                    <Textarea
                      id="ticket-steps"
                      value={stepsTaken}
                      onChange={(e) => setStepsTaken(e.target.value)}
                      rows={3}
                    />
                    <Hint>
                      Knowing your current workaround helps us understand the pain point and prioritize.
                    </Hint>
                  </div>
                </>
              )}

              {/* File attachments */}
              <div className="space-y-1">
                <Label>Screenshots or Screen Recordings</Label>
                <TicketFileDropzone files={files} onFilesChange={setFiles} />
                <Hint>
                  {category === "bug"
                    ? "A screenshot or video of the issue is extremely helpful — especially error messages or unexpected behavior."
                    : "Optional — include mockups or examples of what you have in mind."}
                </Hint>
              </div>

              {/* Submit */}
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="w-full"
              >
                {isSubmitting ? (
                  <>
                    <Spinner className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit Ticket"
                )}
              </Button>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
